// pages/VideoCallPage.jsx

import {
  Eraser,
  Maximize2,
  MessageSquare,
  Mic, MicOff,
  Minus,
  Pencil,
  Send,
  Terminal as TerminalIcon, Trash2,
  Users,
  Video, VideoOff,
  X
} from 'lucide-react';
import Peer from 'peerjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import io from 'socket.io-client';
import { useTheme } from '../../contexts/ThemeContext';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;


// ── WHITEBOARD ─────────────────────────────────────────────────────────────
const Whiteboard = ({ socketRef, roomId, darkMode }) => {
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPos = useRef(null);
  const [tool, setTool] = useState('pen'); // pen | eraser | line | rect
  const [color, setColor] = useState('#22d3ee');
  const [size, setSize] = useState(3);
  const ctxRef = useRef(null);

  const COLORS = ['#22d3ee', '#f97316', '#10b981', '#a855f7', '#ef4444', '#ffffff', '#64748b'];

  useEffect(() => {
  const canvas = canvasRef.current;
  if (!canvas) return;

  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();

  // Set canvas internal resolution higher
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;

  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);          // ← scale le contexte

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctxRef.current = ctx;

  // Fond
  ctx.fillStyle = darkMode ? '#0a0a14' : '#f8fafc';
  ctx.fillRect(0, 0, canvas.width / dpr, canvas.height / dpr); // ← attention /dpr

  // Option très nette : désactiver lissage image si pixel art, mais ici on veut smooth
  // ctx.imageSmoothingEnabled = false; // ← à tester, souvent pire pour drawing
}, [darkMode]);

  // Réception des traits distants
  useEffect(() => {
    if (!socketRef.current) return;
    
    const handleDraw = ({ x0, y0, x1, y1, color, size, tool }) => {
      const ctx = ctxRef.current;
      if (!ctx) return;
      drawLine(ctx, x0, y0, x1, y1, color, size, tool);
    };

    const handleClear = () => {
      const canvas = canvasRef.current;
      const ctx = ctxRef.current;
      if (!canvas || !ctx) return;
      ctx.fillStyle = darkMode ? '#0a0a14' : '#f8fafc';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    };

    socketRef.current.on('whiteboard-draw', handleDraw);
    socketRef.current.on('whiteboard-clear', handleClear);
    return () => {
      socketRef.current?.off('whiteboard-draw', handleDraw);
      socketRef.current?.off('whiteboard-clear', handleClear);
    };
  }, [socketRef.current, darkMode]);

  const drawLine = (ctx, x0, y0, x1, y1, col, sz, tl) => {
    ctx.save(); // ← important pour isoler les changements

    if (tl === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = sz * 3.5; // ← augmente un peu + pour gomme large
      ctx.strokeStyle = 'rgba(0,0,0,1)'; // n'importe quoi, car destination-out
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = col;
      ctx.lineWidth = sz;
    }

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.shadowBlur = 0;           // ← évite halo parasite
    ctx.shadowColor = 'transparent';

    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.stroke();

    ctx.restore(); // ← reset composite & autres
  };

  // Remplace drawLine par une version smooth
const drawSmoothLine = (ctx, points, col, sz, tl) => {
  if (points.length < 4) return;

  ctx.save();
  ctx.globalCompositeOperation = tl === 'eraser' ? 'destination-out' : 'source-over';
  ctx.strokeStyle = col;
  ctx.lineWidth = tl === 'eraser' ? sz * 4 : sz;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);

  for (let i = 1; i < points.length - 2; i += 2) {
    const midX = (points[i].x + points[i + 1].x) / 2;
    const midY = (points[i].y + points[i + 1].y) / 2;
    ctx.quadraticCurveTo(points[i].x, points[i].y, midX, midY);
  }

  // Dernier segment
  if (points.length >= 3) {
    ctx.quadraticCurveTo(
      points[points.length - 2].x,
      points[points.length - 2].y,
      points[points.length - 1].x,
      points[points.length - 1].y
    );
  }

  ctx.stroke();
  ctx.restore();
};

  const getPos = (e) => {
  const canvas = canvasRef.current;
  const rect = canvas.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;
  const clientX = e.touches?.[0]?.clientX ?? e.clientX;
  const clientY = e.touches?.[0]?.clientY ?? e.clientY;
  return {
    x: (clientX - rect.left) * (canvas.width / rect.width) / dpr,  // ← /dpr
    y: (clientY - rect.top) * (canvas.height / rect.height) / dpr,
  };
};

  const onMouseDown = (e) => {
    isDrawing.current = true;
    lastPos.current = getPos(e);
  };

  const onMouseMove = (e) => {
    if (!isDrawing.current) return;
    const pos = getPos(e);
    const ctx = ctxRef.current;
    if (!ctx) return;

    drawLine(ctx, lastPos.current.x, lastPos.current.y, pos.x, pos.y, color, size, tool);

    socketRef.current?.emit('whiteboard-draw', {
      roomId,
      x0: lastPos.current.x, y0: lastPos.current.y,
      x1: pos.x, y1: pos.y,
      color, size, tool
    });

    lastPos.current = pos;
  };

  const onMouseUp = () => { isDrawing.current = false; };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = darkMode ? '#0a0a14' : '#f8fafc';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    socketRef.current?.emit('whiteboard-clear', { roomId });
  };

  return (
    <div className="flex flex-col h-full">
      
      {/* Toolbar */}
      <div className={`shrink-0 flex items-center gap-3 px-4 py-3 border-b ${
        darkMode ? 'border-white/5 bg-black/20' : 'border-slate-200 bg-slate-50'
      }`}>
        
        {/* Outils */}
        <div className="flex gap-1">
          {[
            { id: 'pen', icon: Pencil },
            { id: 'eraser', icon: Eraser },
            { id: 'line', icon: Minus },
          ].map(({ id, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTool(id)}
              className={`p-2 rounded-lg transition-all ${
                tool === id
                  ? darkMode ? 'bg-cyan-500/20 text-cyan-400' : 'bg-blue-100 text-blue-700'
                  : darkMode ? 'text-white/30 hover:text-white/60 hover:bg-white/5' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Icon size={14} />
            </button>
          ))}
        </div>

        <div className={`w-px h-6 ${darkMode ? 'bg-white/5' : 'bg-slate-200'}`} />

        {/* Couleurs */}
        <div className="flex gap-1.5">
          {COLORS.map(c => (
            <button
              key={c}
              onClick={() => setColor(c)}
              className={`w-4 h-4 rounded-full transition-all ${
                color === c ? 'ring-2 ring-offset-1 ring-white/30 scale-125' : 'opacity-60 hover:opacity-100'
              }`}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>

        <div className={`w-px h-6 ${darkMode ? 'bg-white/5' : 'bg-slate-200'}`} />

        {/* Taille */}
        <input
          type="range" min="1" max="20" value={size}
          onChange={(e) => setSize(Number(e.target.value))}
          className="w-20 accent-cyan-500"
        />

        <div className="ml-auto">
          <button
            onClick={handleClear}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${
              darkMode 
                ? 'border border-red-500/20 text-red-400/60 hover:bg-red-500/10 hover:text-red-400' 
                : 'border border-red-200 text-red-500 hover:bg-red-50'
            }`}
          >
            <Trash2 size={11} /> Clear
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 relative overflow-hidden">
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full cursor-crosshair"
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onTouchStart={onMouseDown}
          onTouchMove={onMouseMove}
          onTouchEnd={onMouseUp}
        />
      </div>
    </div>
  );
};

// ── NOTES PARTAGÉES ─────────────────────────────────────────────────────────
const SharedNotes = ({ socketRef, roomId, darkMode }) => {
  const [notes, setNotes] = useState('');
  const [lastEditor, setLastEditor] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!socketRef.current) return;

    const handleUpdate = ({ content, editor }) => {
      setNotes(content);
      setLastEditor(editor);
    };

    socketRef.current.on('notes-update', handleUpdate);
    return () => socketRef.current?.off('notes-update', handleUpdate);
  }, [socketRef.current]);

  const handleChange = (e) => {
    const content = e.target.value;
    setNotes(content);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      socketRef.current?.emit('notes-update', { roomId, content, editor: 'Vous' });
    }, 300);
  };

  return (
    <div className="flex flex-col h-full">
      <div className={`shrink-0 flex items-center justify-between px-4 py-3 border-b ${
        darkMode ? 'border-white/5 bg-black/20' : 'border-slate-200 bg-slate-50'
      }`}>
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span className={`text-[9px] font-black uppercase tracking-widest font-mono ${
            darkMode ? 'text-white/30' : 'text-gray-500'
          }`}>Notes_Partagées</span>
        </div>
        {lastEditor && (
          <span className={`text-[8px] font-mono ${darkMode ? 'text-white/20' : 'text-gray-400'}`}>
            Édité par : {lastEditor}
          </span>
        )}
      </div>
      <div className="flex-1 overflow-hidden">
        <textarea
          value={notes}
          onChange={handleChange}
          placeholder="Prises de notes collaboratives en temps réel..."
          className={`w-full h-full p-5 resize-none outline-none font-mono text-sm leading-relaxed ${
            darkMode 
              ? 'bg-transparent text-white/70 placeholder-white/10' 
              : 'bg-white text-gray-800 placeholder-gray-300'
          }`}
        />
      </div>
    </div>
  );
};

// ── PAGE PRINCIPALE ─────────────────────────────────────────────────────────
const VideoCallPage = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const roomId = searchParams.get('room');
  const titleParam = searchParams.get('title');
  const isLive = roomId?.startsWith('live-');
  const displayTitle = titleParam 
    ? decodeURIComponent(titleParam) 
    : (isLive ? 'Live officiel' : 'Salle collaborative');

  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [remoteParticipants, setRemoteParticipants] = useState({});
  const [screenStream, setScreenStream] = useState(null);

  const localVideoRef = useRef(null);
  const socketRef = useRef(null);
  const peerRef = useRef(null);
  const localStreamRef = useRef(null);
  const videoRefs = useRef({});
  const messagesEndRef = useRef(null);

  // Scroll auto chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Init media
  useEffect(() => {
    const getMedia = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        localStreamRef.current = stream;
        if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      } catch (err) {
        console.error('Accès média refusé :', err);
      }
    };
    getMedia();
    return () => localStreamRef.current?.getTracks().forEach(t => t.stop());
  }, []);

  // Init Socket + Peer
  useEffect(() => {
    if (!roomId) return;

    const socket = io(SOCKET_URL + '/video', { 
      withCredentials: true,
      transports: process.env.NODE_ENV === 'development' ? ['polling'] : ['polling', 'websocket'],
      reconnection:         true,
      reconnectionAttempts: 10,
      reconnectionDelay:    1000,
    });
    socketRef.current = socket;

    const peer = new Peer({
      host: window.location.hostname,
      port: process.env.NODE_ENV === 'production' ? undefined : 5000,
      path: '/',
      secure: window.location.protocol === 'https:',
    });
    peerRef.current = peer;

    peer.on('open', (myPeerId) => {
      socket.emit('join-room', { roomId, peerId: myPeerId });
    });

    peer.on('call', (call) => {
      call.answer(localStreamRef.current);
      call.on('stream', (remoteStream) => addRemoteStream(call.peer, remoteStream));
    });

    socket.on('user-connected', (remotePeerId) => {
      if (!localStreamRef.current) return;
      const call = peer.call(remotePeerId, localStreamRef.current);
      call?.on('stream', (remoteStream) => addRemoteStream(remotePeerId, remoteStream));
    });

    socket.on('existing-peers', (peerIds) => {
      peerIds.forEach((remotePeerId) => {
        if (remotePeerId === peer.id || !localStreamRef.current) return;
        const call = peer.call(remotePeerId, localStreamRef.current);
        call?.on('stream', (remoteStream) => addRemoteStream(remotePeerId, remoteStream));
      });
    });

    socket.on('user-disconnected', (peerId) => {
      setRemoteParticipants(prev => {
        const n = { ...prev };
        delete n[peerId];
        return n;
      });
    });

    socket.on('newMessage', (msg) => setMessages(prev => [...prev, msg]));

    return () => { socket.disconnect(); peer.destroy(); };
  }, [roomId]);


  const addRemoteStream = useCallback((peerId, stream) => {
    setRemoteParticipants(prev => ({
      ...prev,
      [peerId]: { stream, name: `Node_${peerId.slice(-4).toUpperCase()}` }
    }));
  }, []);


  useEffect(() => {
    Object.entries(remoteParticipants).forEach(([peerId, p]) => {
      const el = videoRefs.current[peerId];
      if (el && p.stream && el.srcObject !== p.stream) {
        el.srcObject = p.stream;
      }
    });
  }, [remoteParticipants]);

  const toggleMute = () => {
    localStreamRef.current?.getAudioTracks().forEach(t => t.enabled = !t.enabled);
    setIsMuted(v => !v);
  };

  const toggleVideo = () => {
    localStreamRef.current?.getVideoTracks().forEach(t => t.enabled = !t.enabled);
    setIsVideoOff(v => !v);
  };

  const sendMessage = () => {
    if (!messageInput.trim() || !socketRef.current) return;
    const msg = {
      sender: 'Vous',
      text: messageInput,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      self: true,
    };
    socketRef.current.emit('sendMessage', { ...msg, sender: 'Expert_User' });
    setMessages(prev => [...prev, msg]);
    setMessageInput('');
  };

  const toggleScreenShare = async () => {
    if (screenStream) {
      screenStream.getTracks().forEach(t => t.stop());
      setScreenStream(null);
      // Remettre la webcam dans tous les peer calls actifs
      const videoTrack = localStreamRef.current?.getVideoTracks()[0];
      peerRef.current?._connections && Object.values(peerRef.current._connections)
        .flat()
        .forEach(conn => {
          const sender = conn.peerConnection?.getSenders()
            .find(s => s.track?.kind === 'video');
          if (sender && videoTrack) sender.replaceTrack(videoTrack);
        });
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      setScreenStream(stream);
      const screenTrack = stream.getVideoTracks()[0];

      // Remplacer la piste vidéo dans tous les appels actifs
      Object.values(peerRef.current?._connections || {})
        .flat()
        .forEach(conn => {
          const sender = conn.peerConnection?.getSenders()
            .find(s => s.track?.kind === 'video');
          if (sender) sender.replaceTrack(screenTrack);
        });

      // Arrêt automatique si l'utilisateur clique "Stop" dans le navigateur
      screenTrack.onended = () => toggleScreenShare();
    } catch (err) {
      console.error('Screen share error:', err);
    }
  };

  const toggleFullscreen = () => {
    const el = document.documentElement;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const totalParticipants = Object.keys(remoteParticipants).length + 1;
  const gridCols = totalParticipants === 1 ? 1 : totalParticipants === 2 ? 2 : 3;

  const TABS = [
    { id: 'chat', icon: MessageSquare, label: 'Chat' },
    { id: 'whiteboard', icon: Pencil, label: 'Board' },
    { id: 'notes', icon: TerminalIcon, label: 'Notes' },
    { id: 'participants', icon: Users, label: 'Nodes' },
  ];

  return (
    <div className="fixed inset-0 z-[2000] bg-[#020617] text-slate-200 font-mono flex flex-col overflow-hidden">
      
      {/* HEADER */}
      <header className={`h-14 shrink-0 border-b border-cyan-500/10 bg-black/60 flex items-center justify-between px-6`}>
        <div className="flex items-center gap-4">
          <div className={`p-1.5 rounded-lg border ${
            isLive ? 'border-red-500/30 bg-red-500/10' : 'border-cyan-500/20 bg-cyan-500/5'
          }`}>
            <TerminalIcon size={16} className={isLive ? 'text-red-400' : 'text-cyan-400'} />
          </div>
          <div>
            <h1 className="text-[10px] font-black tracking-[0.25em] uppercase text-white">
              {displayTitle}
            </h1>
            <div className="flex items-center gap-3 mt-0.5">
              {isLive && (
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-[8px] font-black text-red-400 uppercase tracking-widest">Live</span>
                </div>
              )}
              <span className="text-[8px] text-white/20 uppercase tracking-widest">
                {totalParticipants} participant{totalParticipants > 1 ? 's' : ''}
              </span>
              <span className="text-[8px] text-white/10">Room: {roomId}</span>
            </div>
          </div>
        </div>

        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-red-500/20 bg-red-500/5 text-red-400 hover:bg-red-500 hover:text-white transition-all text-[9px] font-black uppercase tracking-widest"
        >
          <X size={14} /> Quitter
        </button>
      </header>

      <main className="flex-1 flex overflow-hidden relative">
        
        {/* GRILLE VIDÉO */}
        <div className={`flex-1 p-4 overflow-hidden grid gap-3 content-start`}
          style={{ 
            gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
            gridAutoRows: totalParticipants === 1 ? '100%' : '1fr'
          }}
        >
          {/* Local */}
          <div className={`relative rounded-2xl overflow-hidden bg-slate-900 border ${
            isMuted || isVideoOff ? 'border-red-500/20' : 'border-white/5'
          } group`}>
            <video ref={localVideoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute bottom-3 left-3 flex items-center gap-2">
              <div className="px-2 py-1 rounded bg-cyan-500 text-black text-[8px] font-black uppercase tracking-widest">
                Vous
              </div>
              {isMuted && <div className="p-1 rounded bg-red-500/80"><MicOff size={10} className="text-white" /></div>}
              {isVideoOff && <div className="p-1 rounded bg-red-500/80"><VideoOff size={10} className="text-white" /></div>}
            </div>
          </div>

          {/* Distants */}
          {Object.entries(remoteParticipants).map(([peerId, p]) => (
            <div key={peerId} className="relative rounded-2xl overflow-hidden bg-slate-900 border border-cyan-500/10">
              <video
                ref={el => { videoRefs.current[peerId] = el; if (el && p.stream) el.srcObject = p.stream; }}
                autoPlay playsInline className="w-full h-full object-cover"
              />
              <div className="absolute bottom-3 left-3 px-2 py-1 rounded bg-black/60 text-white text-[8px] font-black uppercase tracking-widest backdrop-blur">
                {p.name}
              </div>
            </div>
          ))}

          {Object.keys(remoteParticipants).length === 0 && (
            <div className="flex flex-col items-center justify-center opacity-20 gap-4">
              <Users size={48} className="text-cyan-500" />
              <p className="text-[9px] font-black uppercase tracking-[0.3em] text-cyan-400">
                En attente de participants
              </p>
            </div>
          )}
        </div>

        {/* CONTRÔLES FLOTTANTS */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-50">
          <div className="flex items-center gap-3 px-6 py-4 bg-[#020617]/90 backdrop-blur-xl border border-white/8 rounded-2xl shadow-2xl">
            <button
              onClick={toggleMute}
              className={`p-3 rounded-xl transition-all ${
                isMuted 
                  ? 'bg-red-500 text-white' 
                  : 'bg-white/5 text-slate-300 hover:bg-white/10'
              }`}
            >
              {isMuted ? <MicOff size={18} /> : <Mic size={18} />}
            </button>
            <button
              onClick={toggleVideo}
              className={`p-3 rounded-xl transition-all ${
                isVideoOff 
                  ? 'bg-red-500 text-white' 
                  : 'bg-white/5 text-slate-300 hover:bg-white/10'
              }`}
            >
              {isVideoOff ? <VideoOff size={18} /> : <Video size={18} />}
            </button>
            <div className="w-px h-6 bg-white/5 mx-1" />
            <button onClick={toggleScreenShare} className="px-5 py-2.5 rounded-xl bg-white/5 text-slate-400 hover:bg-white/10 text-[9px] font-black uppercase tracking-widest transition-all">
              Partage_Écran
            </button>
            <button onClick={toggleFullscreen} className="p-3 rounded-xl bg-white/5 ...">
              <Maximize2 size={16} />
            </button>
          </div>
        </div>

        {/* SIDEBAR OUTILS */}
        <aside className="w-80 border-l border-white/5 bg-black/40 flex flex-col shrink-0 ml-3           /* ou ml-4 / ml-6 selon le goût */
    mt-3           /* petit espace en haut aussi souvent apprécié */
    mb-3           /* symétrie bas */
    rounded-2xl    /* très tendance en 2025-2026 */
    overflow-hidden
    shadow-[0_4px_30px_rgba(0,0,0,0.4)]">
          
          {/* Tabs */}
          <div className="grid grid-cols-4 border-b border-white/5 shrink-0">
            {TABS.map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex flex-col items-center py-4 gap-1.5 transition-all relative ${
                  activeTab === id 
                    ? 'text-cyan-400' 
                    : 'text-slate-600 hover:text-slate-400'
                }`}
              >
                <Icon size={16} />
                <span className="text-[8px] font-black uppercase tracking-widest">{label}</span>
                {activeTab === id && (
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-cyan-500 shadow-[0_0_8px_rgba(34,211,238,0.6)]" />
                )}
              </button>
            ))}
          </div>

          {/* Contenu tab */}
          <div className="flex-1 overflow-hidden">
            
            {/* CHAT */}
            {activeTab === 'chat' && (
              <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto p-4 space-y-4 no-scrollbar">
                  {messages.length === 0 && (
                    <div className="h-full flex flex-col items-center justify-center opacity-15 gap-3">
                      <MessageSquare size={36} />
                      <p className="text-[9px] font-black uppercase tracking-widest">Canal_Inactif</p>
                    </div>
                  )}
                  {messages.map((m, i) => (
                    <div key={i} className={`flex flex-col ${m.self ? 'items-end' : 'items-start'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[8px] font-black text-cyan-600/70 uppercase tracking-widest">
                          {m.self ? 'Vous' : m.sender}
                        </span>
                        <span className="text-[7px] text-white/10">{m.time}</span>
                      </div>
                      <div className={`px-3 py-2.5 rounded-xl max-w-[90%] text-[11px] leading-relaxed border ${
                        m.self 
                          ? 'bg-cyan-500/10 border-cyan-500/20 text-cyan-100 rounded-tr-none' 
                          : 'bg-white/5 border-white/5 text-slate-300 rounded-tl-none'
                      }`}>
                        {m.text}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                <div className="p-4 border-t border-white/5 bg-black/20">
                  <div className="flex gap-2">
                    <input
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                      placeholder="Message..."
                      className="flex-1 bg-white/5 border border-white/8 rounded-xl px-4 py-2.5 text-[11px] outline-none focus:border-cyan-500/40 placeholder-white/10 transition-all"
                    />
                    <button
                      onClick={sendMessage}
                      className="p-2.5 bg-cyan-600 hover:bg-cyan-500 rounded-xl transition-all"
                    >
                      <Send size={14} className="text-black" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* WHITEBOARD */}
            {activeTab === 'whiteboard' && (
              <Whiteboard socketRef={socketRef} roomId={roomId} darkMode={darkMode} />
            )}

            {/* NOTES */}
            {activeTab === 'notes' && (
              <SharedNotes socketRef={socketRef} roomId={roomId} darkMode={darkMode} />
            )}

            {/* PARTICIPANTS */}
            {activeTab === 'participants' && (
              <div className="p-4 space-y-2">
                {/* Moi */}
                <div className={`flex items-center gap-3 p-3 rounded-xl border ${
                  darkMode ? 'border-cyan-500/20 bg-cyan-500/5' : 'border-blue-200 bg-blue-50'
                }`}>
                  <div className="w-8 h-8 rounded-lg bg-cyan-500 flex items-center justify-center text-black text-[10px] font-black">
                    V
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] font-black text-white uppercase tracking-wide">Vous</p>
                    <p className="text-[8px] text-white/30 font-mono">Host</p>
                  </div>
                  <div className="flex gap-1">
                    {isMuted && <MicOff size={12} className="text-red-400" />}
                    {isVideoOff && <VideoOff size={12} className="text-red-400" />}
                  </div>
                </div>
                
                {/* Distants */}
                {Object.entries(remoteParticipants).map(([peerId, p]) => (
                  <div key={peerId} className="flex items-center gap-3 p-3 rounded-xl border border-white/5 bg-white/[0.02]">
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-white/50 text-[10px] font-black">
                      {p.name.slice(-2)}
                    </div>
                    <div className="flex-1">
                      <p className="text-[10px] font-black text-white/70 uppercase tracking-wide">{p.name}</p>
                      <p className="text-[8px] text-white/20 font-mono">Participant</p>
                    </div>
                  </div>
                ))}

                {Object.keys(remoteParticipants).length === 0 && (
                  <p className="text-center text-[9px] font-mono text-white/10 uppercase tracking-widest py-8">
                    Aucun participant distant
                  </p>
                )}
              </div>
            )}
          </div>
        </aside>
      </main>

      {/* FOOTER */}
      <footer className="h-8 border-t border-white/5 bg-black flex items-center px-6 justify-between shrink-0">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.5)]" />
            <span className="text-[8px] font-black text-emerald-500/60 uppercase tracking-[0.2em]">Stream_Active</span>
          </div>
          <div className="flex items-center gap-4 text-white/15 text-[8px] uppercase tracking-widest">
            <span>Lat: 18ms</span>
            <span>Loss: 0.00%</span>
            <span>Enc: AES-GCM</span>
          </div>
        </div>
        <span className="text-[8px] font-mono text-cyan-500/20 tracking-widest">Lab_Engine_v2</span>
      </footer>
    </div>
  );
};

export default VideoCallPage;